/* ==========================================================================
   anlass.js — Progressive Enhancement für das Anlass-Collection-Template
   --------------------------------------------------------------------------
   Einziger Zweck: die eTrusted-Sterne in den Produktkarten erst dann in den
   DOM hängen, wenn die Karte in Sichtnähe kommt.

   Hintergrund: snippets/product-item.liquid:104-111 rendert pro Karte
   unkonditioniert ein <etrusted-widget>. Bei einem vollen Grid initialisiert
   integrations.etrusted.com/applications/widget.js/v2 (layout/theme.liquid:435)
   dann alle Widgets gleichzeitig beim Laden — messbare Zeit im Main Thread,
   ausgerechnet auf Mobile.

   <etrusted-widget> ist ein Custom Element. Wird es nach der Definition in den
   DOM eingefügt, upgradet der Browser es automatisch — dasselbe Prinzip, das
   sections/trusted-product-reviews.liquid:60-90 beim Variantenwechsel nutzt.

   Ohne JavaScript erscheinen schlicht keine Sterne. Alle kaufentscheidenden
   Informationen (Titel, Preis, Verfügbarkeit) stehen serverseitig im HTML.
   ========================================================================== */

(function () {
  'use strict';

  var MOUNTED_ATTR = 'data-anlass-stars-mounted';
  var hasObserver = typeof window.IntersectionObserver === 'function';
  var observer = null;

  function mount(el) {
    if (!el || el.getAttribute(MOUNTED_ATTR)) {
      return;
    }

    var widgetId = el.getAttribute('data-widget-id');
    var sku = el.getAttribute('data-sku');

    if (!widgetId || !sku) {
      return;
    }

    el.setAttribute(MOUNTED_ATTR, '1');

    var widget = document.createElement('etrusted-widget');
    widget.className = 'etrusted-sku-widget-stars';
    widget.setAttribute('data-etrusted-widget-id', widgetId);
    widget.setAttribute('data-sku', sku);
    el.appendChild(widget);
  }

  if (hasObserver) {
    observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            mount(entries[i].target);
            observer.unobserve(entries[i].target);
          }
        }
      },
      { rootMargin: '300px 0px' }
    );
  }

  /**
   * Nimmt alle noch nicht bestückten Platzhalter innerhalb von `root` in Betrieb.
   * Bewusst als Funktion mit Wurzel-Parameter statt als einmaliger Durchlauf:
   * Der Theme-Editor tauscht bei jeder Änderung das Section-DOM aus. Eine beim
   * Laden eingesammelte NodeList zeigt danach auf verworfene Knoten, und in den
   * neu gerenderten Karten würden nie Sterne erscheinen.
   */
  function init(root) {
    if (!root || typeof root.querySelectorAll !== 'function') {
      return;
    }

    var placeholders = root.querySelectorAll('[data-anlass-stars]');

    for (var i = 0; i < placeholders.length; i++) {
      if (placeholders[i].getAttribute(MOUNTED_ATTR)) {
        continue;
      }
      if (hasObserver) {
        observer.observe(placeholders[i]);
      } else {
        mount(placeholders[i]);
      }
    }
  }

  /* ========================================================================
     Einleitungstext auf Mobilgeräten kürzen

     Gemessen: die volle Collection-Beschreibung braucht ~264px und schiebt
     zusammen mit der USP-Leiste die erste Produktkarte auf 1.099px — über
     eine ganze Viewport-Höhe.

     Bewusst als Progressive Enhancement: serverseitig steht der vollständige
     Text im HTML. Erst dieses Skript kürzt ihn visuell und ergänzt den
     Aufklapp-Button. Ohne JavaScript ist der Text also ungekürzt lesbar —
     nie abgeschnitten ohne Möglichkeit, ihn zu öffnen. Crawler und AI-Agenten
     sehen ihn ohnehin immer vollständig, `line-clamp` versteckt nur optisch.
     ======================================================================== */

  var narrow = window.matchMedia('(max-width: 640px)');

  function updateClamp(el) {
    var btn = el.__anlassToggle;
    if (!btn) {
      return;
    }

    if (!narrow.matches) {
      el.setAttribute('data-anlass-clamped', 'false');
      btn.hidden = true;
      return;
    }

    // Vom gekürzten Zustand aus messen, ob überhaupt etwas abgeschnitten wird.
    if (el.getAttribute('data-anlass-clamped') !== 'false' || !el.__anlassOpened) {
      el.setAttribute('data-anlass-clamped', 'true');
      btn.hidden = el.scrollHeight <= el.clientHeight + 1;
    }
  }

  function setupClamp(el) {
    if (el.__anlassToggle) {
      return;
    }

    var lines = parseInt(el.getAttribute('data-anlass-clamp'), 10);
    el.style.setProperty('--anlass-clamp-lines', lines > 0 ? lines : 3);

    var more = el.getAttribute('data-anlass-clamp-more') || 'Mehr anzeigen';
    var less = el.getAttribute('data-anlass-clamp-less') || 'Weniger anzeigen';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'anlass-hero__introToggle';
    btn.textContent = more;
    btn.setAttribute('aria-expanded', 'false');
    btn.hidden = true;

    if (!el.id) {
      el.id = 'anlass-intro-' + Math.round(el.getBoundingClientRect().top) + '-' + el.textContent.length;
    }
    btn.setAttribute('aria-controls', el.id);

    btn.addEventListener('click', function () {
      var isClamped = el.getAttribute('data-anlass-clamped') !== 'false';
      el.setAttribute('data-anlass-clamped', isClamped ? 'false' : 'true');
      el.__anlassOpened = isClamped;
      btn.setAttribute('aria-expanded', isClamped ? 'true' : 'false');
      btn.textContent = isClamped ? less : more;
    });

    el.parentNode.insertBefore(btn, el.nextSibling);
    el.__anlassToggle = btn;
    updateClamp(el);
  }

  function initClamps(root) {
    if (!root || typeof root.querySelectorAll !== 'function') {
      return;
    }
    var targets = root.querySelectorAll('[data-anlass-clamp]');
    for (var i = 0; i < targets.length; i++) {
      setupClamp(targets[i]);
    }
  }

  function refreshClamps() {
    var targets = document.querySelectorAll('[data-anlass-clamp]');
    for (var i = 0; i < targets.length; i++) {
      updateClamp(targets[i]);
    }
  }

  if (typeof narrow.addEventListener === 'function') {
    narrow.addEventListener('change', refreshClamps);
  } else if (typeof narrow.addListener === 'function') {
    narrow.addListener(refreshClamps);
  }

  init(document);
  initClamps(document);

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    initClamps(event.target);
  });
})();
